import React, { forwardRef, useState, useEffect } from 'react';
import { checkWebPSupport, checkWebPExists } from '../../utils/webpSupport';
import './styles.css';

/**
 * Componente otimizado para exibição de imagens com suporte automático para WebP
 * e recursos de responsividade mobile-first
 * 
 * @param {string} src - Caminho da imagem
 * @param {string} alt - Texto alternativo para a imagem
 * @param {string} className - Classes adicionais
 * @param {object} style - Estilos inline
 * @param {function} onLoad - Callback quando a imagem carregar
 * @param {function} onError - Callback quando ocorrer erro
 * @param {string} loading - Estratégia de carregamento (lazy, eager)
 * @param {string} aspectRatio - Proporção de aspecto (1:1, 4:3, 16:9)
 * @param {boolean} responsive - Se a imagem deve aplicar tamanhos responsivos
 * @param {boolean} fullWidth - Se a imagem deve ocupar 100% da largura
 */
const OptimizedImage = forwardRef(({
  src,
  alt = '',
  className = '',
  style = {},
  onLoad,
  onError,
  loading = 'lazy',
  aspectRatio = null,
  responsive = false,
  fullWidth = false,
  ...props
}, ref) => {
  const [webpSupported, setWebpSupported] = useState(null);
  const [webpExists, setWebpExists] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);
  
  // Gerar as classes para o container
  const getContainerClasses = () => {
    const classes = ['optimized-image-container'];
    
    // Adicionar classe para proporção de aspecto
    if (aspectRatio) {
      classes.push(`aspect-ratio-${aspectRatio.replace(':', '-')}`);
    }
    
    return classes.join(' ');
  };
  
  // Gerar as classes para a imagem
  const getImageClasses = () => {
    const classes = ['optimized-image'];
    
    // Adicionar classe de estado de carregamento
    if (imageLoaded) {
      classes.push('loaded');
    }
    
    // Adicionar classe para responsividade
    if (responsive) {
      classes.push('optimized-image-responsive');
    }
    
    // Adicionar classe para largura total
    if (fullWidth) {
      classes.push('optimized-image-fullwidth');
    }
    
    // Adicionar classes personalizadas
    if (className) {
      classes.push(className);
    }
    
    return classes.join(' ');
  };
  
  // Verificar suporte a WebP uma vez quando o componente montar
  useEffect(() => {
    let isMounted = true;
    
    checkWebPSupport().then(supported => {
      if (isMounted) {
        setWebpSupported(supported);
      }
    });
    
    return () => {
      isMounted = false;
    };
  }, []);
  
  // Verificar se existe uma versão WebP da imagem
  useEffect(() => {
    if (webpSupported && src && typeof src === 'string') {
      let isMounted = true;
      
      // Calcular caminho da versão WebP
      const webpSrc = src.replace(/\.(png|jpg|jpeg)$/i, '.webp');
      
      // Verificar se o arquivo WebP existe
      checkWebPExists(webpSrc).then(exists => {
        if (isMounted) {
          setWebpExists(exists);
        }
      });
      
      return () => {
        isMounted = false;
      };
    }
  }, [src, webpSupported]);
  
  // Manipuladores de eventos
  const handleImageLoad = (e) => {
    setImageLoaded(true);
    setHasError(false);
    if (onLoad) onLoad(e);
  };
  
  const handleImageError = (e) => {
    setHasError(true);
    if (onError) onError(e);
  };
  
  // Se não tiver src válido, não renderiza nada
  if (!src) {
    return null;
  }
  
  // Se houve erro no carregamento, mostrar mensagem
  if (hasError) {
    return (
      <div className="optimized-image-error">
        Não foi possível carregar a imagem
      </div>
    );
  }
  
  // Se o suporte a WebP é confirmado e temos uma versão WebP
  if (webpSupported && webpExists && typeof src === 'string') {
    const webpSrc = src.replace(/\.(png|jpg|jpeg)$/i, '.webp');
    
    // Renderizar com tag <picture> para fallback automático
    return (
      <picture className={getContainerClasses()}>
        <source srcSet={webpSrc} type="image/webp" />
        <source srcSet={src} type={`image/${src.endsWith('.png') ? 'png' : 'jpeg'}`} />
        <img
          ref={ref}
          src={src}
          alt={alt}
          style={style}
          loading={loading}
          onLoad={handleImageLoad}
          onError={handleImageError}
          className={getImageClasses()}
          {...props}
        />
        {!imageLoaded && (
          <div className="optimized-image-placeholder" aria-hidden="true">
            <div className="loading-spinner"></div>
          </div>
        )}
      </picture>
    );
  }
  
  // Fallback para imagem normal quando WebP não é suportado ou não existe
  return (
    <div className={getContainerClasses()}>
      <img
        ref={ref}
        src={src}
        alt={alt}
        style={style}
        loading={loading}
        onLoad={handleImageLoad}
        onError={handleImageError}
        className={getImageClasses()}
        {...props}
      />
      {!imageLoaded && (
        <div className="optimized-image-placeholder" aria-hidden="true">
          <div className="loading-spinner"></div>
        </div>
      )}
    </div>
  );
});

export default OptimizedImage; 